import { useEffect, useRef } from "react";
import { useUser } from "../contexts/UserContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useUserSettings } from "../contexts/UserSettingsContext";
import useCycleData from "./useCycleData";
import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";

const isNative = Capacitor.isNativePlatform();
let notifIdCounter = 5000; // offset to avoid collision with task notifications

async function sendDeviceNotif(title: string, body: string) {
  // Native only — on web the cloud function (`sendCyclePredictionReminders`,
  // `onPeriodCompleted`, etc.) handles the FCM push, so firing here too would
  // double-notify the user.
  if (!isNative) return;
  await LocalNotifications.schedule({
    notifications: [{ title, body, id: notifIdCounter++, smallIcon: "ic_stat_notify" }],
  });
}

/**
 * Client-side cycle notifications:
 * - Period starting soon (1-2 days before predicted)
 * - Period predicted today
 * - Fertile window starting
 * - Period late reminder
 * Runs once per session; deduplicates via localStorage.
 */
const useCycleNotifications = () => {
  const { userDetails } = useUser()!;
  const { addNotification } = useNotifications();
  const { settings } = useUserSettings();
  const {
    cycleSettings,
    periodEntries,
    currentPeriod,
    nextPrediction,
    ovulationWindow,
    cycleEnabled,
    loading,
  } = useCycleData();

  const hasRun = useRef(false);

  useEffect(() => {
    if (loading || hasRun.current) return;
    if (!settings.notifications) return;
    if (!(settings.notificationPrefs?.cycle ?? true)) return;
    if (!userDetails?.user?.uid) return;
    if (!cycleEnabled || !cycleSettings) return;

    hasRun.current = true;
    const uid = userDetails.user!.uid;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = today.toISOString().split("T")[0];

    const dedupKey = (type: string) => `cycleNotif_${uid}_${type}_${todayKey}`;
    const alreadySent = (type: string) => localStorage.getItem(dedupKey(type)) === "1";
    const markSent = (type: string) => localStorage.setItem(dedupKey(type), "1");

    const send = (type: string, title: string, body: string, notifType: "info" | "reminder") => {
      if (alreadySent(type)) return;
      markSent(type);
      addNotification(title, body, notifType);
      sendDeviceNotif(title, body);
    };

    // 1. Period prediction notifications
    if (nextPrediction && !currentPeriod) {
      const predStart = nextPrediction.startDate;
      const diffMs = predStart.getTime() - today.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        send("period_today", "Period day", "Your period is predicted to start today.", "reminder");
      } else if (diffDays === 1) {
        send("period_tomorrow", "Period coming", "Your period is predicted to start tomorrow.", "info");
      } else if (diffDays === 2) {
        send("period_2days", "Period in 2 days", "Your period is predicted to start in 2 days.", "info");
      } else if (diffDays < 0) {
        // Period is late
        const lateDays = Math.abs(diffDays);
        if (lateDays >= 1 && lateDays <= 7) {
          send(
            "period_late",
            "Period late",
            `Your period is ${lateDays} day${lateDays > 1 ? "s" : ""} late.`,
            "reminder"
          );
        }
      }
    }

    // 2. Fertile window notification
    if (ovulationWindow && !currentPeriod) {
      const ovStart = ovulationWindow.start;
      const ovDiffMs = ovStart.getTime() - today.getTime();
      const ovDiffDays = Math.round(ovDiffMs / (1000 * 60 * 60 * 24));

      if (ovDiffDays === 0) {
        send("fertile_today", "Fertile window", "Your fertile window starts today.", "info");
      } else if (ovDiffDays === 1) {
        send("fertile_tomorrow", "Fertile window soon", "Your fertile window starts tomorrow.", "info");
      }
    }

    // 3. Log reminder — if not on period and they have a tracking history
    if (!currentPeriod && periodEntries.length > 0) {
      send("log_reminder", "Daily log", "Don't forget to log how you're feeling today!", "info");
    }

    // Cleanup old dedup keys (older than 7 days)
    try {
      const keys = Object.keys(localStorage).filter((k) => k.startsWith("cycleNotif_"));
      for (const k of keys) {
        const parts = k.split("_");
        const dateStr = parts[parts.length - 1];
        const d = new Date(dateStr + "T00:00:00");
        if (!isNaN(d.getTime()) && today.getTime() - d.getTime() > 7 * 86400000) {
          localStorage.removeItem(k);
        }
      }
    } catch {
      // ignore
    }
  }, [
    loading,
    settings.notifications,
    userDetails,
    cycleEnabled,
    cycleSettings,
    nextPrediction,
    currentPeriod,
    ovulationWindow,
    periodEntries,
    addNotification,
  ]);
};

export default useCycleNotifications;
